import React from 'react';
import { CheckIcon, ClockIcon, CopyIcon, KeyRoundIcon, ListChecksIcon, ShieldCheckIcon, SparklesIcon, ZapIcon } from 'lucide-react';
import { CtaButton } from '../components/CtaButton';
import { FaqItem } from '../components/FaqItem';
import { TestimonialMarquee } from '../components/TestimonialMarquee';
import { bonuses, faqs, features, formatPillars, heroIncludes, offerChecklist, outcomes, painPoints, volumeTitles } from '../data/content';
const PROPERTY_PHOTOS = [{
  src: 'https://cdn.magicpatterns.com/patterns/generated-images/7ef33bee-248f-49f7-a730-1bebfc7eccc0.jpg',
  alt: 'Bright short-term rental living room set up for guests'
}, {
  src: 'https://cdn.magicpatterns.com/patterns/generated-images/8d43bdc7-aedb-4321-99eb-b20dc6e1cd36.jpg',
  alt: 'Guest bedroom with fresh linens and rolled towels'
}, {
  src: 'https://cdn.magicpatterns.com/patterns/generated-images/668ed66c-a840-4847-92ba-7ba8030faa84.jpg',
  alt: 'Rental kitchen with a coffee station and welcome basket'
}];
const EARNINGS_SCREEN_URL = 'https://cdn.magicpatterns.com/patterns/generated-images/2aa52dc9-dc9d-4f4b-a126-0973dac23848.jpg';
const SUPERHOST_BADGE_URL = 'https://cdn.magicpatterns.com/patterns/generated-images/a4743bf0-6b49-4cc5-af3b-67e6fdeffe65.jpg';
const EBOOK_COVER_URL = 'https://cdn.magicpatterns.com/uploads/hxHwPrBoY1eq7ZgNm6dDNQ/Mockup_The_Hands_Off_Hosting_System.png';
const LOGO_URL = 'https://cdn.magicpatterns.com/uploads/1roViPmDXjaiz1sGkRNVUW/Logo_IG.jpg';
const phases = [{
  label: 'Welcome',
  description: 'Answer every guest question before it is asked.'
}, {
  label: 'Protect',
  description: 'House rules and safety that hold up under pressure.'
}, {
  label: 'Impress',
  description: 'Local recommendations that earn written reviews.'
}, {
  label: 'Automate',
  description: 'Self check-in and check-out that run without you.'
}, {
  label: 'Maintain',
  description: 'One written standard for every single turnover.'
}, {
  label: 'Resolve',
  description: 'Emergency scripts so guests solve it first.'
}];
export function SalesPage() {
  return <div className="min-h-screen w-full overflow-x-hidden bg-brand-cream font-body text-slate-800" data-id="element-24" data-mp="2ajwhh2o0id2a">

      {/* Top banner */}
      <header data-id="element-25" data-mp="1vn5j8wq2r7e9">
        <div className="bg-brand-medium px-4 py-2 text-center text-xs font-bold tracking-wide text-white sm:text-sm" data-id="element-26" data-mp="1vlm110kuym9u">
          <span aria-hidden="true" data-id="element-27" data-mp="20k5mz0zgby27">🔑 </span>Digital download — Instant access after purchase
        </div>
        
      </header>

      <main id="top" data-id="element-28" data-mp="1msha2vhdp5k6">

        {/* Hero */}
        <section className="bg-brand-deep px-5 py-8 text-center text-white sm:py-14 lg:py-16" data-id="element-29" data-mp="1rm3pgvexfnjo">
          <div className="mx-auto max-w-4xl" data-id="element-30" data-mp="1cx78u9yt127g">
            <h1 className="font-display text-4xl font-bold leading-[1.08] tracking-tight sm:text-5xl lg:text-7xl" data-id="element-31" data-mp="1cu1m31dnecew">
              Do you manage a temporary rental?{' '}
              <span className="text-brand-gold" data-id="element-32" data-mp="13wratg0tyn9z">This is for you</span>
            </h1>
            <p className="mx-auto mt-3 max-w-2xl text-base leading-relaxed text-white/85 sm:mt-4 sm:text-xl" data-id="element-33" data-mp="17w01ud4hk71u">
              <strong className="font-black text-white" data-id="element-34" data-mp="p9k63s1xuljj">The Hands-Off Hosting System.</strong> The guide that automates your business and turns you into a Superhost, recovering your freedom!
            </p>
            <img src={EBOOK_COVER_URL} alt="The complete Hands-Off Hosting System — six volumes plus 5 bonuses included" className="mx-auto mt-3 w-full max-w-[280px] bg-transparent object-contain sm:mt-5 sm:max-w-md lg:max-w-lg" data-id="element-35" data-mp="yb0gw0wo0poa" />
            <div className="mt-4 sm:mt-6" data-id="element-36" data-mp="185aaflxcfu0l">
              <CtaButton label="YES, I WANT HANDS-OFF HOSTING!" data-id="element-37" data-mp="137h3urs4oxzs" />
              <p className="mt-3 text-xs font-bold tracking-wide text-white/70" data-id="element-38" data-mp="1gzx8ty4lb63c">
                🔒 Secure checkout in your local currency
              </p>
            </div>

            {/* What you get */}
            <div className="mx-auto mt-8 max-w-2xl text-left sm:mt-10" data-id="element-39" data-mp="1qxuwajemkyuw">
              <p className="text-center text-sm font-black uppercase tracking-[0.16em] text-brand-gold" data-id="element-40" data-mp="1qvryhd78e1fm">
                Here is everything you get
              </p>
              <ul className="mt-4 grid gap-2 sm:grid-cols-2" role="list" data-id="element-41" data-mp="260cvzzxcujb8">
                {heroIncludes.map(item => <li key={item} className="flex items-start gap-3 rounded-xl border border-white/10 bg-white/5 px-4 py-3" data-id="element-42" data-mp="2a1eugejg6rc5">
                    <CheckIcon className="mt-0.5 h-5 w-5 flex-none text-brand-gold" aria-hidden="true" data-id="element-43" data-mp="2evj455k3ldgx" />
                    <span className="text-[15px] leading-snug text-white/90" data-id="element-44" data-mp="2judm65a8qxzz">{item}</span>
                  </li>)}
              </ul>

              <p className="mt-7 text-center text-sm font-black uppercase tracking-[0.16em] text-brand-gold" data-id="element-45" data-mp="1r3c0rmfyg680">
                The 6 volumes inside
              </p>
              <ul className="mt-4 space-y-2" role="list" data-id="element-46" data-mp="1w51cl4b04nx5">
                {volumeTitles.map(volume => <li key={volume} className="flex items-start gap-3 border-l-2 border-brand-gold/60 pl-4 text-[15px] leading-snug text-white/90" data-id="element-47" data-mp="213cqbq4f3ikn">
                    {volume}
                  </li>)}
              </ul>
            </div>
          </div>
        </section>

        {/* Format promise bar */}
        <section aria-label="Format promise" className="bg-[#EAE2D0] px-5 py-4 text-center text-sm font-bold sm:text-base text-brand-deep" data-id="element-48" data-mp="10vxtv3hdxisl">
          <span aria-hidden="true" data-id="element-49" data-mp="r3jp01v0fw27">⚡ </span>Zero fluff, 100% actionable dashboards and copy-paste templates — implement in under 2 hours
        </section>

        {/* Pain points */}
        <section id="pain-points" className="bg-brand-cream px-5 py-12 sm:py-16" data-id="element-50" data-mp="i3dhcwlztqay">
          <div className="mx-auto max-w-5xl" data-id="element-51" data-mp="i1airo6ryoum">
            <div className="mx-auto max-w-2xl text-center" data-id="element-52" data-mp="d40rs43qm54o">
              <p className="text-sm font-black uppercase tracking-[0.16em] text-brand-medium" data-id="element-53" data-mp="d6mxrzazccks">
                You are not alone
              </p>
              <h2 className="mt-3 font-display text-4xl font-bold tracking-tight text-brand-deep sm:text-5xl" data-id="element-54" data-mp="375wrv6oamv5">
                Does any of this sound familiar?
              </h2>
            </div>
            <ul className="mt-8 grid gap-4 sm:grid-cols-2 sm:gap-5" role="list" data-id="element-55" data-mp="1xl6weia1rdwa">
              {painPoints.map(point => <li key={point.text} className="flex items-start gap-4 rounded-2xl border border-brand-medium/15 bg-white p-5 shadow-sm" data-id="element-56" data-mp="3rzkel7v3kmgp">
                  <span className="mt-0.5 text-2xl" aria-hidden="true" data-id="element-57" data-mp="nhnifp2p2ke">{point.emoji}</span>
                  <span className="text-base leading-relaxed text-slate-700" data-id="element-58" data-mp="3i03b8x3biknw">{point.text}</span>
                </li>)}
            </ul>
            <blockquote className="mx-auto mt-10 max-w-3xl border-l-4 border-brand-medium pl-5 font-display text-xl font-bold italic leading-relaxed text-brand-deep sm:text-2xl" data-id="element-59" data-mp="elpgkciznjqa">
              "You do not have a hosting problem. You have a missing operating system for your property."
            </blockquote>
            <p className="mx-auto mt-5 max-w-3xl text-center text-base leading-relaxed text-slate-600" data-id="element-60" data-mp="9ltj6w4sqqjp">
              Most hosts answer the same twenty questions for every booking, explain check-in from scratch each time, and rely on memory for turnovers. That is not hospitality — it is unpaid admin. Documented systems are what separate a hands-off property from a second job.
            </p>
          </div>
        </section>

        {/* Solution */}
        <section id="solution" className="bg-brand-deep px-5 py-12 text-white sm:py-16" data-id="element-61" data-mp="2hw2ipn4ll25e">
          <div className="mx-auto max-w-3xl text-center" data-id="element-62" data-mp="2wqh4fvfjqpnr">
            <KeyRoundIcon className="mx-auto h-10 w-10 text-brand-gold" aria-hidden="true" data-id="element-63" data-mp="2rrw29qgqhvwo" />
            <p className="mt-5 text-sm font-black uppercase tracking-[0.16em] text-brand-gold" data-id="element-64" data-mp="2421s5gnsulg4">The solution</p>
            <h2 className="mt-3 font-display text-4xl font-bold leading-tight tracking-tight sm:text-5xl" data-id="element-65" data-mp="1z49r9x56fozs">
              You don&apos;t need to work harder. You need a documented system.
            </h2>
            <div className="mt-5 space-y-4 text-lg leading-relaxed text-white/85" data-id="element-66" data-mp="290mrldv8913i">
              <p data-id="element-67" data-mp="1lbk4hqhtqg1m">
                The Hands-Off Hosting System is not another list of hosting tips. It is a complete 6-volume operating manual for your property: the guides guests read, the rules that protect you, and the standards your cleaner follows.
              </p>
              <p data-id="element-68" data-mp="zsqyluanwd2p">
                <strong className="font-bold text-white" data-id="element-69" data-mp="zs8ru3qdiggj">Set it up once</strong> and every future guest gets the same seamless stay — without you answering another midnight message.
              </p>
            </div>
            <div className="mt-8 grid gap-3 text-left sm:grid-cols-2" data-id="element-70" data-mp="18tp1285gccj6">
              {phases.map(phase => <div key={phase.label} className="rounded-xl border border-white/15 bg-white/5 p-4" data-id="element-71" data-mp="1dtbi6soeicx9">
                  <p className="text-sm font-black uppercase tracking-wide text-brand-gold" data-id="element-72" data-mp="1iqutbjpsy9kz">{phase.label}</p>
                  <p className="mt-1 text-sm leading-relaxed text-white/80" data-id="element-73" data-mp="1nje9mofvjith">{phase.description}</p>
                </div>)}
            </div>
          </div>
        </section>

        {/* Format — not a book to read */}
        <section id="format" className="bg-brand-cream px-5 py-12 sm:py-16" data-id="element-74" data-mp="1wkm5kzux47ke">
          <div className="mx-auto max-w-5xl" data-id="element-75" data-mp="21hchrc3lqj0j">
            <div className="mx-auto max-w-2xl text-center" data-id="element-76" data-mp="21g1j1qp0k1ga">
              <p className="text-sm font-black uppercase tracking-[0.16em] text-brand-medium" data-id="element-77" data-mp="3m180j8q9qi6y">
                Zero fluff, 100% actionable
              </p>
              <h2 className="mt-3 font-display text-4xl font-bold tracking-tight text-brand-deep sm:text-5xl" data-id="element-78" data-mp="3czgv84zn4ddm">
                This is not a book you read. It is a system you install.
              </h2>
              <p className="mt-4 text-lg leading-relaxed text-slate-700" data-id="element-79" data-mp="8l0jry0kv12f">
                No chapters, no theory, no homework. You open a page, copy what is on it into your property, and move on.
              </p>
            </div>
            <div className="mt-8 grid gap-4 md:grid-cols-3" data-id="element-80" data-mp="3vxays3i01wom">
              {formatPillars.map((pillar, index) => {
              const Icon = [ClockIcon, CopyIcon, ListChecksIcon][index];
              return <article key={pillar.label} className="flex flex-col rounded-2xl border border-brand-medium/15 bg-white p-6 shadow-sm" data-id="element-81" data-mp="saax55cup9xd" data-mpi={index}>
                    <Icon className="h-7 w-7 text-brand-medium" aria-hidden="true" data-id="element-82" data-mp="ngg77k6njypu" />
                    <h3 className="mt-4 font-display text-xl font-bold leading-snug text-brand-deep" data-id="element-83" data-mp="31ei2j3w1k5qs">
                      {pillar.label}
                    </h3>
                    <p className="mt-2 text-[15px] leading-relaxed text-slate-600" data-id="element-84" data-mp="3af6g5xhiw435">
                      {pillar.description}
                    </p>
                  </article>;
            })}
            </div>
            <p className="mx-auto mt-8 max-w-2xl rounded-2xl bg-brand-deep px-6 py-4 text-center text-base font-black text-white sm:text-lg" data-id="element-85" data-mp="3kc2iflgtvtz9">
              Implement in under 2 hours. Hands-off from your next check-in.
            </p>
          </div>
        </section>

        {/* What's inside */}
        <section id="inside" className="bg-[#E7E0D1] px-5 py-12 sm:py-16" data-id="element-86" data-mp="3p4vms88dx0at">
          <div className="mx-auto max-w-6xl" data-id="element-87" data-mp="6xo9htdhl34b">
            <div className="mx-auto max-w-2xl text-center" data-id="element-88" data-mp="6yh8g867eobw">
              <p className="text-sm font-black uppercase tracking-[0.16em] text-brand-medium" data-id="element-89" data-mp="1zw7u7n1i2mx">
                Fill-in-the-blank templates, ready to use
              </p>
              <h2 className="mt-3 font-display text-4xl font-bold tracking-tight text-brand-deep sm:text-5xl" data-id="element-90" data-mp="qjzjhww6mift">
                The 6 Volumes of the System
              </h2>
            </div>
            <div className="mt-8 grid gap-4 md:grid-cols-2 lg:grid-cols-3" data-id="element-91" data-mp="cmkp0dr2nkgz">
              {features.map((feature, index) => <article key={feature.title} className="rounded-2xl border border-brand-medium/15 bg-white p-5 shadow-sm" data-id="element-92" data-mp="2uext68n972u" data-mpi={index}>
                  <span className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-brand-medium font-display text-lg font-bold text-white" data-id="element-93" data-mp="7lewoh1bpz7z">
                    {String(index + 1).padStart(2, '0')}
                  </span>
                  <h3 className="mt-5 font-display text-xl font-bold leading-snug text-brand-deep" data-id="element-94" data-mp="3u0zlcfzhpwv4">
                    {feature.title}
                  </h3>
                  <p className="mt-2 text-xs font-black uppercase tracking-wide text-brand-medium" data-id="element-95" data-mp="3l0b9nrxkk8le">
                    {feature.pain}
                  </p>
                  <p className="mt-3 text-[15px] leading-relaxed text-slate-600" data-id="element-96" data-mp="1qlnyd5w6gcr2">
                    {feature.description}
                  </p>
                </article>)}
            </div>
          </div>
        </section>

        {/* Bonuses */}
        <section id="bonuses" className="bg-[#F1EADB] px-5 py-12 sm:py-16" data-id="element-97" data-mp="1ct7szyft02mz">
          <div className="mx-auto max-w-5xl" data-id="element-98" data-mp="29ifc6zsy9znq">
            <div className="mx-auto max-w-2xl text-center" data-id="element-99" data-mp="29i5mzefqbwj3">
              <p className="text-sm font-black uppercase tracking-[0.16em] text-brand-medium" data-id="element-100" data-mp="24j2tmr943eqy">
                Included at no extra cost
              </p>
              <h2 className="mt-3 font-display text-4xl font-bold tracking-tight text-brand-deep sm:text-5xl" data-id="element-101" data-mp="20k3qnc6xxs25">
                🎁 5 Free Bonuses Included
              </h2>
              <p className="mt-4 text-lg leading-relaxed text-slate-700" data-id="element-102" data-mp="p3lr3381r9ba">
                Everything you need to hand your property over to a system today.
              </p>
            </div>
            <div className="mt-8 grid gap-4 md:grid-cols-2" data-id="element-103" data-mp="2vs5q4l9e14dw">
              {bonuses.map(bonus => <article key={bonus.title} className="rounded-r-2xl border-l-4 border-brand-medium bg-white px-5 py-5 shadow-sm sm:px-6" data-id="element-104" data-mp="30pe8alujd8f5">
                  <div className="flex items-start gap-4" data-id="element-105" data-mp="34qhlf0l78qco">
                    <span className="text-3xl" aria-hidden="true" data-id="element-106" data-mp="39ir6o3bdd28m">{bonus.emoji}</span>
                    <div data-id="element-107" data-mp="3ei3uaqa2edc9">
                      <span className="inline-block rounded-full bg-brand-gold px-3 py-1 text-xs font-black uppercase tracking-wide text-brand-deep" data-id="element-108" data-mp="3egj2h8f4x8i3">
                        {bonus.tag}
                      </span>
                      <h3 className="mt-3 font-display text-xl font-bold leading-snug text-brand-deep" data-id="element-109" data-mp="3og9th04hl5df">
                        {bonus.title}
                      </h3>
                      <p className="mt-2 text-[15px] leading-relaxed text-slate-600" data-id="element-110" data-mp="1u2ipoh8u9fpw">
                        {bonus.description}
                      </p>
                    </div>
                  </div>
                </article>)}
            </div>
          </div>
        </section>

        {/* Proof */}
        <section id="proof" className="bg-brand-deep px-5 py-12 text-white sm:py-16" data-id="element-111" data-mp="ysc2hxs9itgm">
          <div className="mx-auto max-w-6xl" data-id="element-112" data-mp="1crv9gn9c7yrc">
            <div className="mx-auto max-w-2xl text-center" data-id="element-113" data-mp="1cqk7mszgd12v">
              <p className="text-sm font-black uppercase tracking-[0.16em] text-brand-gold" data-id="element-114" data-mp="17rz7suo44jey">
                Real properties, real numbers
              </p>
              <h2 className="mt-3 font-display text-4xl font-bold tracking-tight sm:text-5xl" data-id="element-115" data-mp="k0blyr5pjyjj">
                Hosts running the system
              </h2>
            </div>
            <div className="mt-8 grid gap-4 lg:grid-cols-3" data-id="element-116" data-mp="tu26xm2mlquh">
              <figure className="overflow-hidden rounded-2xl border border-white/10 bg-white/5 lg:col-span-2" data-id="element-117" data-mp="p196hae6x4o7">
                <div className="grid grid-cols-2 gap-px bg-white/10 sm:grid-cols-3" data-id="element-118" data-mp="1b50iwdj4su1">
                  {PROPERTY_PHOTOS.map(photo => <img key={photo.src} src={photo.src} alt={photo.alt} className="h-40 w-full object-cover sm:h-48" data-id="element-119" data-mp="3shogsr24xazq" />)}
                  <div className="col-span-2 flex items-center gap-4 bg-brand-deep p-5 sm:col-span-3" data-id="element-120" data-mp="27ycdm4hnbo46">
                    <img src={SUPERHOST_BADGE_URL} alt="Superhost recognition badge" className="h-16 w-16 flex-none rounded-xl object-cover" data-id="element-121" data-mp="2ct8jo8jpwc0a" />
                    <figcaption className="text-left" data-id="element-122" data-mp="230uawvu8254m">
                      <p className="font-display text-xl font-bold text-white" data-id="element-123" data-mp="22z9ltlqofee5">
                        Built to get you Superhost status
                      </p>
                      <p className="mt-1 text-sm leading-relaxed text-white/75" data-id="element-124" data-mp="2voq8s3vj232z">
                        Consistent guest experience, faster responses, and cleaner turnovers — the exact metrics the badge is scored on.
                      </p>
                    </figcaption>
                  </div>
                </div>
              </figure>
              <figure className="flex flex-col overflow-hidden rounded-2xl border border-white/10 bg-white/5" data-id="element-125" data-mp="16dfyd9rousbv">
                <img src={EARNINGS_SCREEN_URL} alt="Host dashboard showing monthly earnings, 94% occupancy and a 4.98 rating" className="h-64 w-full object-cover lg:h-full lg:flex-1" data-id="element-126" data-mp="3czc7oi2d0fuw" />
                <figcaption className="p-5 text-left text-sm leading-relaxed text-white/75" data-id="element-127" data-mp="382ljf7geidye">
                  <span className="block font-black text-brand-gold" data-id="element-128" data-mp="337g26lxy1ngn">Fewer messages, better reviews</span>
                  Hosts using the system report calmer months, higher occupancy, and ratings that hold near 5.0.
                </figcaption>
              </figure>
            </div>
          </div>
        </section>

        {/* Testimonials */}
        <section id="testimonials" className="bg-[#F1EADB] py-12 sm:py-16" data-id="element-129" data-mp="2k9wpn8ca1mph">
          <div className="mx-auto max-w-2xl px-5 text-center" data-id="element-130" data-mp="2g9crq2jvqf2a">
            <p className="text-sm font-black uppercase tracking-[0.16em] text-brand-medium" data-id="element-131" data-mp="2gb5z5k2bh3bc">
              What hosts say
            </p>
            <h2 className="mt-3 font-display text-4xl font-bold tracking-tight text-brand-deep sm:text-5xl" data-id="element-132" data-mp="ehzpxmd7knag">
              Set up once. Hosting got quiet.
            </h2>
          </div>
          <div className="mt-8" data-id="element-133" data-mp="21efxnmur98b2">
            <TestimonialMarquee data-id="element-134" data-mp="21debam8agze1" />
          </div>
        </section>

        {/* Author */}
        <section id="author" className="bg-brand-cream px-5 py-12 sm:py-16" data-id="element-135" data-mp="264wap5unyrww">
          <div className="mx-auto flex max-w-4xl flex-col items-center gap-7 text-center md:flex-row md:text-left" data-id="element-136" data-mp="2a6hxbix02kka">
            <img src={LOGO_URL} alt="Prof. Kyra logo" className="h-40 w-40 flex-none rounded-full border-8 border-white bg-black object-contain shadow-lg" data-id="element-137" data-mp="1dh8vk6c5wthn" />
            <div data-id="element-138" data-mp="1ihoa3186k717">
              <p className="text-sm font-black uppercase tracking-[0.16em] text-brand-medium" data-id="element-139" data-mp="1mh5gpxzcfa7y">
                Created with care
              </p>
              <h2 className="mt-3 font-display text-4xl font-bold tracking-tight text-brand-deep sm:text-5xl" data-id="element-140" data-mp="1rh1hlocqulja">
                Meet Prof. Kyra
              </h2>
              <p className="mt-5 text-lg leading-relaxed text-slate-700" data-id="element-141" data-mp="32v5eb2apuns1">
                Prof. Kyra creates practical, field-tested digital guides designed to solve real everyday problems. Every resource is built to give real tools — not just information.
              </p>
              <p className="mt-4 text-lg leading-relaxed text-slate-700" data-id="element-142" data-mp="29hxiliudzfpu">
                The Hands-Off Hosting System was created for hosts who want a calm, professional operation instead of a phone that never stops buzzing.
              </p>
            </div>
          </div>
        </section>

        {/* Outcomes */}
        <section id="outcomes" className="bg-[#E7E0D1] px-5 py-12 sm:py-16" data-id="element-143" data-mp="2sai5mjnemqlq">
          <div className="mx-auto max-w-4xl text-center" data-id="element-144" data-mp="2scm7kw1qtfjd">
            <p className="text-sm font-black uppercase tracking-[0.16em] text-brand-medium" data-id="element-145" data-mp="368q10n4sljus">
              What you will achieve
            </p>
            <h2 className="mt-3 font-display text-4xl font-bold tracking-tight text-brand-deep sm:text-5xl" data-id="element-146" data-mp="1bw055zuv60kr">
              After the System
            </h2>
            <p className="mx-auto mt-5 max-w-3xl font-display text-xl font-bold italic leading-relaxed text-brand-deep" data-id="element-147" data-mp="17vpx8hnih039">
              "A great stay is not improvised. It is the result of a system written down once."
            </p>
            <ul className="mt-8 grid gap-4 text-left sm:grid-cols-2" role="list" data-id="element-148" data-mp="11tw6hhfbipsy">
              {outcomes.map(outcome => <li key={outcome} className="flex items-start gap-3 rounded-2xl border border-brand-medium/15 bg-white p-5 shadow-sm" data-id="element-149" data-mp="szrjxa77w0j8">
                  <CheckIcon className="mt-0.5 h-5 w-5 flex-none text-brand-olive" aria-hidden="true" data-id="element-150" data-mp="e27gw8tvsffp" />
                  <span className="text-base leading-relaxed text-slate-700" data-id="element-151" data-mp="j2mu90e5xmxo">{outcome}</span>
                </li>)}
            </ul>
          </div>
        </section>

        {/* Offer */}
        <section id="offer" className="bg-brand-deep px-5 py-12 sm:py-16" data-id="element-152" data-mp="a0dosoxr4jr5">
          <div className="mx-auto max-w-xl rounded-3xl bg-white p-7 text-center shadow-2xl sm:p-8" data-id="element-153" data-mp="1zftt9r0qdhap">
            <SparklesIcon className="mx-auto h-9 w-9 text-brand-medium" aria-hidden="true" data-id="element-154" data-mp="24ewlubzixv1s" />
            <h2 className="mt-4 font-display text-3xl font-bold text-brand-deep sm:text-4xl" data-id="element-155" data-mp="37z2xf48jlp9d">
              Everything You Get Today
            </h2>
            <p className="mt-2 font-display text-2xl font-bold text-brand-deep sm:text-3xl" data-id="element-156" data-mp="3cqkz5uie3tve">
              The Hands-Off Hosting System
            </p>
            <ul className="mt-7 space-y-3 text-left" role="list" data-id="element-157" data-mp="32zzrlhkqnf0c">
              {offerChecklist.map(item => <li key={item} className="flex items-start gap-3 text-[15px] leading-snug text-slate-700" data-id="element-158" data-mp="3qpknf11zq6r1">
                  <CheckIcon className="mt-0.5 h-5 w-5 flex-none text-brand-olive" aria-hidden="true" data-id="element-159" data-mp="3vof3hv8kph7g" />
                  <span data-id="element-160" data-mp="3hp5xtphzu5hq">{item}</span>
                </li>)}
            </ul>
            <div className="mt-8 flex flex-col items-center border-t border-brand-medium/15 pt-7 text-center" data-id="element-161" data-mp="3hqyzeqdr1ziv">
              <p className="text-base font-bold text-slate-500 line-through" data-id="element-162" data-mp="269qqpsw2lx8w">99 USD</p>
              <p className="mt-1 font-display text-5xl font-bold leading-none text-brand-deep" data-id="element-163" data-mp="26c3j6zzgfq07">
                23 <span className="align-middle text-2xl" data-id="element-164" data-mp="2a8zkuyv0vx4z">USD</span>
              </p>
              <p className="mt-2 text-sm font-bold text-slate-600" data-id="element-165" data-mp="22pyqb3279dtr">
                One-time payment — Yours forever
              </p>
            </div>
            <p className="mt-6 text-sm font-black text-brand-medium" data-id="element-166" data-mp="2cqirk5ppyibz">
              Buy today, implement in under 2 hours — hands-off by your next check-in.
            </p>
            <CtaButton label="YES, I WANT HANDS-OFF HOSTING!" className="mt-3 w-full" target="checkout" data-id="element-167" data-mp="1k1blmlnxqqgp" />
            <p className="mt-3 text-xs font-bold tracking-wide text-slate-600" data-id="element-168" data-mp="1f28nzmaabmir">
              🔒 Secure checkout in your local currency
            </p>
            <p className="mt-2 text-xs font-bold tracking-wide text-slate-500" data-id="element-169" data-mp="1tsodvednbin9">
              📩 Instant delivery <span aria-hidden="true" data-id="element-170" data-mp="1oyty2m15vntg">|</span> 🛡️ 7-day guarantee
            </p>
          </div>
        </section>

        {/* FAQ */}
        <section id="faq" className="bg-brand-cream px-5 py-12 sm:py-16" data-id="element-171" data-mp="wbr0px881j4s">
          <div className="mx-auto max-w-3xl" data-id="element-172" data-mp="wbhbibv03g05">
            <div className="text-center" data-id="element-173" data-mp="1m0en0lanew4q">
              <p className="text-sm font-black uppercase tracking-[0.16em] text-brand-medium" data-id="element-174" data-mp="1lzd6i1av5hfm">
                Clear answers
              </p>
              <h2 className="mt-3 font-display text-4xl font-bold tracking-tight text-brand-deep sm:text-5xl" data-id="element-175" data-mp="1usgjy9lidua9">
                Frequently Asked Questions
              </h2>
            </div>
            <div className="mt-8 space-y-3" data-id="element-176" data-mp="1310nzoy2av7d">
              {faqs.map(faq => <FaqItem key={faq.question} {...faq} data-id="element-177" data-mp="132lixfoagg5r" />)}
            </div>
          </div>
        </section>

        {/* Final CTA */}
        <section id="final-cta" className="bg-brand-deep px-5 py-12 text-center text-white sm:py-16" data-id="element-178" data-mp="174fdd67w4nxh">
          <div className="mx-auto max-w-3xl" data-id="element-179" data-mp="1h0s7243fr1s8">
            <ZapIcon className="mx-auto h-10 w-10 text-brand-gold" aria-hidden="true" data-id="element-180" data-mp="1h1l6sl3z8r0v" />
            <h2 className="mt-5 font-display text-4xl font-bold tracking-tight sm:text-5xl" data-id="element-181" data-mp="2nfsu1kahg9mo">
              Hand the work to a system. Host hands-off.
            </h2>
            <p className="mx-auto mt-5 max-w-2xl text-lg leading-relaxed text-white/85" data-id="element-182" data-mp="gslpj0kfmmo5">
              If you set the system up and it does not reduce the guest messages, check-in calls, and turnover chaos in your hosting week, we will refund 100% of your investment. No questions. No hassle.
            </p>
            <p className="mt-5 text-sm font-black text-brand-gold" data-id="element-183" data-mp="23z5iljv16ql">
              Now with the full 6×1 system!
            </p>
            <CtaButton label="YES, I WANT HANDS-OFF HOSTING!" className="mt-3" data-id="element-184" data-mp="3u8jcsdpvdei8" />
            <p className="mt-3 text-xs font-bold tracking-wide text-white/70" data-id="element-185" data-mp="3p7vnpxl7b7qn">
              🔒 Secure checkout in your local currency
            </p>
          </div>
        </section>

      </main>

      <footer className="bg-[#0C1729] px-5 py-10 text-center text-white/70" data-id="element-186" data-mp="3faqzls7sp277">
        <ShieldCheckIcon className="mx-auto h-6 w-6 text-brand-gold" aria-hidden="true" data-id="element-187" data-mp="1ihyx998jxq2r" />
        <p className="mt-4 text-sm" data-id="element-188" data-mp="1ifl63bgzsy0h">© 2026 Prof. Kyra · profkyra@gmail.com</p>
        <nav aria-label="Footer navigation" className="mt-3 flex flex-wrap justify-center gap-x-5 gap-y-2 text-sm" data-id="element-189" data-mp="1djnneyi9bfkj">
          <a href="#privacy" className="underline-offset-4 hover:text-white hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-gold" data-id="element-190" data-mp="1lhpp9je8j26v">
            Privacy Policy
          </a>
          <a href="#terms" className="underline-offset-4 hover:text-white hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-gold" data-id="element-191" data-mp="1gkh3l8tvog0v">
            Terms of Service
          </a>
          <a href="mailto:profkyra@gmail.com" className="underline-offset-4 hover:text-white hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-gold" data-id="element-192" data-mp="17jspke4hifnz">
            Contact
          </a>
        </nav>
        <p className="mx-auto mt-6 max-w-3xl text-xs leading-relaxed text-white/55" data-id="element-193" data-mp="xt6hd4z3krfb">
          This product is an educational and informational resource for property hosts. It does not constitute legal, tax, or insurance advice.
        </p>
      </footer>

    </div>;
}